import { useEffect, useMemo, useState, useTransition } from "react";
import { defaultEditorDefns } from "../../__dummy__/TestEditor";
import styles from "../aic.module.css";
import { EditorDefns } from "../types/interface";
import { fromHtml, toHtml } from "../utility";
import { classMerge } from "../utility/classMerge";
import { AioDropSelect } from "./aioDropSelect";
import { AioIconButton } from "./aioIconButton";
import { AioReplacementValuesDisplay } from "./aioReplacementValuesDisplay";
import { AioExternalReplacements, AioReplacement, AioReplacementValues } from "./interface";
import { newReplacementValues } from "./newReplacementValues";

interface AioReplacmentDisplayProps<T> {
  id: string;
  airid?: string;
  oldText: string;
  newTexts: AioReplacementValues<T>[];
  includeTrailing?: boolean;
  externalName?: string;
  setReplacement?: (ret: AioReplacement<T>) => void;
  externalLists?: AioExternalReplacements<T>[];
  dontAskSpace?: boolean;
  dontAskTrail?: boolean;
  noText?: boolean;
  editorProps?: EditorDefns<T>;
  level: number;
}

/**
 * Render an individuial AioReplacement.
 *
 * @param props - Destructured parameter object
 * @param props.id - The id for the component
 * @param props.airid - The airid of the replacement
 * @param props.oldText - The oldText to be replaced
 * @param props.newTexts - The newTexts to be used as replacements
 * @param props.includeTrailing - The includeTrailing boolean
 * @param props.externalName - The externalName for the replacement
 * @param props.setReplacement - The setReplacement function
 * @param props.externalLists - The externalLists available
 * @param props.dontAskSpace - The dontAskSpace boolean
 * @param props.dontAskTrail - The dontAskTrail boolean
 * @param props.noText - The noText boolean
 * @param props.editorProps - The editorProps to configure the text editor
 * @param props.level - The current nesting level
 */
export const AioReplacementDisplay = <T,>({
  id,
  airid,
  oldText,
  newTexts,
  includeTrailing,
  externalName,
  setReplacement,
  externalLists,
  dontAskSpace,
  dontAskTrail,
  noText,
  editorProps = defaultEditorDefns as EditorDefns<T>,
  level,
}: AioReplacmentDisplayProps<T>): React.ReactElement => {
  const [_isPending, startTransition] = useTransition();
  const [displayText, setDisplayText] = useState<string>(fromHtml(oldText));
  useEffect(() => {
    startTransition(() => setDisplayText(fromHtml(oldText)));
  }, [oldText]);

  const availableListNames = useMemo<string[]>(() => {
    const a: string[] = ["with..."];
    const exl: string[] = [];
    externalLists?.map((rep) => {
      exl.push(rep.givenName);
      return true;
    });
    a.push(...exl.sort((a, b) => a.localeCompare(b)));
    return a;
  }, [externalLists]);

  /**
   * Return updated replacement object
   *
   * @param newReplacement - Partial replacement object with updated values
   */
  const returnData = (newReplacement: Partial<AioReplacement<T>>) => {
    /* istanbul ignore else */
    if (setReplacement) {
      // Create new object
      const r: AioReplacement<T> = {
        airid: airid ?? crypto.randomUUID(),
        oldText,
        newTexts,
        includeTrailing,
        externalName,
        ...newReplacement,
      };
      // Remove default
      if (r.externalName === "with...") delete r.externalName;

      // Update existing object
      setReplacement(r);
    }
  };

  /**
   * Add new text entry
   *
   * @param i - Index to add at
   */
  const addNewText = (i: number) => {
    /* istanbul ignore else */
    if (setReplacement) {
      const nts = [...newTexts];
      nts.splice(i, 0, newReplacementValues(editorProps.blankT));
      returnData({ newTexts: nts });
    }
  };
  /**
   * Remove new text entry
   *
   * @param i - Index to remove at
   */
  const removeNewText = (i: number) => {
    /* istanbul ignore else */
    if (setReplacement) {
      const nts = [...newTexts];
      nts.splice(i, 1);
      returnData({ newTexts: nts });
    }
  };

  return (
    <div
      id={id}
      className={styles.replacementContainer}
    >
      {!noText && (
        <div>
          {typeof setReplacement !== "function" ? (
            <span>{`${oldText}`.trim() !== "" ? fromHtml(oldText) : <em>Nothing</em>}</span>
          ) : (
            <input
              id={`${id}-input`}
              className={classMerge([styles.aioInput, styles.width170])}
              type="text"
              value={displayText}
              onChange={(e) => setDisplayText(e.currentTarget.value)}
              onBlur={(e) => {
                returnData({ oldText: toHtml(e.currentTarget.value) });
              }}
            />
          )}
          {!dontAskTrail && (
            <div className={styles.replacementButtonArea}>
              <label htmlFor={`${id}-includetrailing`}>
                <small>Include trailing rows</small>
              </label>
              <input
                id={`${id}-includetrailing`}
                disabled={typeof setReplacement !== "function"}
                className={styles.margin6}
                type="checkbox"
                checked={includeTrailing}
                onChange={(e) => returnData({ includeTrailing: e.currentTarget.checked })}
              />
            </div>
          )}
        </div>
      )}
      {typeof setReplacement === "function" &&
      externalLists !== undefined &&
      externalLists.length > 0 ? (
        <div>
          <AioDropSelect
            id={`${id}-dropselect`}
            value={!externalName ? "with..." : externalName}
            availableValues={availableListNames}
            setValue={(ret) => {
              returnData({ externalName: ret });
            }}
          />
        </div>
      ) : externalName !== undefined && externalName !== "" ? (
        <div className={styles.fontItalic}>{externalName}</div>
      ) : null}
      <div>
        {externalLists?.some((e) => e.givenName === externalName) ? (
          <>
            {externalLists
              ?.find((e) => e.givenName === externalName)
              ?.newTexts.map((e) => (
                <AioReplacementValuesDisplay
                  id={`${id}-subdisplay`}
                  key={e.airid}
                  airid={e.airid}
                  texts={e.texts}
                  spaceAfter={e.spaceAfter}
                  subLists={e.subLists}
                  editorProps={editorProps}
                  level={level}
                />
              ))}
          </>
        ) : (
          <>
            {newTexts.map((rv, i) => (
              <div key={`${i}-${rv.airid}`}>
                <AioReplacementValuesDisplay
                  id={`${id}-subdisplay`}
                  key={rv.airid}
                  airid={rv.airid}
                  texts={rv.texts}
                  spaceAfter={rv.spaceAfter}
                  subLists={rv.subLists}
                  externalLists={externalLists}
                  dontAskSpace={dontAskSpace}
                  dontAskTrail={typeof setReplacement === "function" ? dontAskTrail : true}
                  setReplacementValue={
                    typeof setReplacement === "function"
                      ? (ret) => {
                          const nts = [...newTexts];
                          nts.splice(i, 1, ret);
                          returnData({ newTexts: nts });
                        }
                      : undefined
                  }
                  editorProps={editorProps}
                  level={level}
                />
                {typeof setReplacement === "function" && (
                  <div
                    className={classMerge([styles.aioxButtonHolder, styles.replacementRowIndented])}
                  >
                    {newTexts && newTexts.length > 1 && (
                      <AioIconButton
                        id={`${id}-remove`}
                        iconName={"aioxRemoveUp"}
                        onClick={() => removeNewText(i)}
                        tipText={"Remove new text"}
                      />
                    )}
                    <AioIconButton
                      id={`${id}-add`}
                      iconName={"aioxAddDown"}
                      onClick={() => addNewText(i + 1)}
                      tipText={"Add new text"}
                    />
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

AioReplacementDisplay.displayName = "AioReplacementDisplay";
